import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowForward } from '@mui/icons-material';

interface QuickAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  color?: string;
}

interface QuickActionTileProps {
  title?: string;
  actions: QuickAction[];
}

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const QuickActionTile: React.FC<QuickActionTileProps> = ({
  title = 'Quick Actions',
  actions,
}) => {
  return (
    <Box
      component={motion.div}
      variants={itemVariants}
      sx={{
        borderRadius: '20px',
        background: 'rgba(26, 26, 26, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        {actions.map((action, index) => (
          <Box
            key={index}
            component={motion.div}
            whileHover={{ scale: 1.02, x: 4 }}
            whileTap={{ scale: 0.98 }}
            onClick={action.onClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.06)',
                borderColor: action.color
                  ? `${action.color}40`
                  : 'rgba(74, 158, 255, 0.3)',
                '& .action-arrow': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: action.color
                  ? `${action.color}15`
                  : 'rgba(74, 158, 255, 0.15)',
                color: action.color || '#4a9eff',
              }}
            >
              {action.icon}
            </Box>

            <Typography
              variant="body2"
              sx={{ fontWeight: 500, flex: 1 }}
            >
              {action.label}
            </Typography>

            <ArrowForward
              className="action-arrow"
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                opacity: 0,
                transform: 'translateX(-8px)',
                transition: 'all 0.2s ease',
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QuickActionTile;
